import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

import { AssociateSiteVisitQueueRow } from '../../services/site-visit.service';
import { statusLabel, statusPillClass } from '../../site-visit/site-visit-state.util';
import { renderVisitDay, renderVisitTime } from '../../site-visit/site-visit-time.util';
import {
  PENDING_MULTI_ACTION_LABEL,
  SchedulingQueueRowVm,
  actorContextOf,
  canRescheduleRow,
  operativeDateOf,
  pendingActionLabel,
} from '../scheduling-queue.util';

/**
 * The DOM id of a site-visit row, so the `?visitId=` deep link and the element it reveals cannot drift
 * apart.
 *
 * 🔴 `scrollToAnchorWhenReady` finds the row with `document.getElementById`, so the string the caller
 * scrolls to and the string the template binds MUST be the same one. They used to be one method on the
 * scheduling page; now that the row is a component, they are one exported function instead of a literal
 * in each place. A drift here fails nowhere visible — every other assertion about the row stays green and
 * the associate simply lands at the top of the queue the mail was meant to skip.
 */
export function siteVisitAnchorId(visitId: string): string {
  return 'site-visit-' + visitId;
}

/**
 * The transient highlight applied to a revealed row. Defined here because the rule that animates it lives
 * in this component's stylesheet, and applied imperatively by the surface that scrolls — see
 * `scrollToAnchorWhenReady`.
 */
export const QUEUE_ROW_FLASH_CLASS = 'queue-row--flash';

/**
 * One site visit as a row: its state in plain language, where and when it is, and its one pending action.
 *
 * **Presentational, and deliberately so.** It derives everything it renders from the row it is handed and
 * owns no data fetching, no transition and no route. Clicking either control emits; the surface hosting
 * the row decides what opening means and projects the opened detail into it. That split is what lets the
 * cross-project scheduling queue and the workspace's site-visit card render the *same* row rather than two
 * that agree today — §5.1 chose *supplement*, not *replace*, so a state must not read one way on one
 * surface and another way on the other.
 *
 * 🔴 Every label, pill and action word below is derived from `site-visit-state.util.ts` (the port of
 * connect-service's `SiteVisitTransitionEnum`) through `scheduling-queue.util.ts`. There is no second
 * state map here and there must not be one: a future state change lands in the transition table and every
 * surface follows it.
 */
@Component({
  selector: 'app-scheduling-queue-row',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './scheduling-queue-row.component.html',
  styleUrl: './scheduling-queue-row.component.scss',
})
export class SchedulingQueueRowComponent {
  /** The row to render, already derived — see `scheduling-queue.util.ts`. */
  @Input({ required: true }) vm!: SchedulingQueueRowVm;

  /** Whether this row is the expanded one. Drives the open styling, the toggle's label and `aria-expanded`. */
  @Input() open = false;

  /**
   * Render state only — no controls at all.
   *
   * 🔴 This suppresses **both** buttons, not just the pending action. *View Details* is what opens the
   * negotiation panel, and that panel carries every state's transitions, so a live toggle on a
   * view-only surface would offer writes the surface exists to withhold. Hiding the action alone would
   * look right and be wrong.
   *
   * Defaults to `false`, so the cross-project scheduling queue — which is never view-only — binds
   * nothing and behaves exactly as before. The workspace's site-visit card sets it, passing through the
   * `readOnly` the `/project-history/:id` route already hands the card.
   */
  @Input() readOnly = false;

  /**
   * The pending move was clicked. Every state's action does the same thing from the row — it opens the
   * row — so this and {@link toggle} are wired to the same handler today; they stay separate because the
   * two controls are separate, not because they differ.
   */
  @Output() action = new EventEmitter<SchedulingQueueRowVm>();

  /** The View Details / Close control was clicked. */
  @Output() toggle = new EventEmitter<SchedulingQueueRowVm>();

  /**
   * The row's own *Reschedule* control was clicked — its own output rather than a third emitter of
   * {@link action}, and that is the whole mechanism behind opening the panel with the reschedule form
   * already showing.
   *
   * 🔴 It exists because `action` cannot answer "which control was pressed?". Both hosts wire
   * `(action)="open(vm)"` for the pending move AND wired it for this button too, so the host saw one
   * indistinguishable event and could only ever open the panel generically. A flag on `action`'s
   * payload would have carried the same information, but every other control's wiring would then have
   * had to learn about a field it never sets; a separate output leaves `(action)`/`(toggle)` exactly
   * as they were and adds one line per host.
   *
   * The row still performs nothing — this opens the panel, as `action` does. What differs is only what
   * the host records about WHY it opened, which it passes to `<site-visit-negotiation>`.
   */
  @Output() reschedule = new EventEmitter<SchedulingQueueRowVm>();

  /** The row's DOM anchor — what the `?visitId=` deep link reveals. */
  get anchorId(): string {
    return siteVisitAnchorId(this.vm.row.id);
  }

  // --- row rendering (deriving, never re-formatting) ----------------------------------------------

  /**
   * The state in plain language.
   *
   * 🔴 The actor context is passed, and it is load-bearing: `ROUTED_TO_PM_LABEL` overrides
   * `PENDING_SITE_CONTACT_APPROVAL` to "Awaiting project manager approval" when the client opted out of
   * automated site-contact scheduling. Calling `statusLabel(status)` without it would tell the PA they
   * are waiting on a site contact who was never contacted.
   */
  label(row: AssociateSiteVisitQueueRow): string {
    return statusLabel(row.status, actorContextOf(row));
  }

  pillClass(row: AssociateSiteVisitQueueRow): string {
    return statusPillClass(row.status);
  }

  /** "Site 2", or nothing when the row carries no site number. */
  siteText(row: AssociateSiteVisitQueueRow): string {
    return row.siteNumber === null || row.siteNumber === undefined
      ? ''
      : `Site ${row.siteNumber}`;
  }

  /**
   * The operative day. Rendered from the stored UTC-noon calendar day read back in UTC — the inverse of
   * how it was pinned, not a conversion. The row projection carries no server-rendered day string, so
   * this is the sanctioned fallback path in `site-visit-time.util.ts` and no zone is involved.
   */
  dayText(row: AssociateSiteVisitQueueRow): string {
    return renderVisitDay(null, operativeDateOf(row));
  }

  /**
   * 🕐 The arrival time on the table: the confirmed one once there is one, the proposed one before that.
   * **Both arrive pre-rendered** — dual-zone, anchored on the agreed day — so this only composes
   * `primary (secondary)` and never re-formats, converts, or synthesizes an abbreviation.
   */
  timeText(row: AssociateSiteVisitQueueRow): string {
    return (
      renderVisitTime(row.displayConfirmedTime) ||
      renderVisitTime(row.displayProposedTime)
    );
  }

  /**
   * The row's one inline action, labelled for what clicking it does.
   *
   * A single pending move keeps that move's own words — the button opens the panel, but the panel then
   * offers exactly that one thing, so naming it is accurate and more useful than a generic verb. More
   * than one pending move gets the panel's words instead: the row is not where the choice between them
   * is made, and a button labelled for one of two moves would name the wrong one half the time.
   */
  actionLabel(vm: SchedulingQueueRowVm): string {
    return vm.pending.length === 1
      ? pendingActionLabel(vm.pending[0])
      : PENDING_MULTI_ACTION_LABEL;
  }

  /** Matches {@link actionLabel}: the transition's id where it names one, the panel's where it does not. */
  actionTestId(vm: SchedulingQueueRowVm): string {
    return vm.pending.length === 1
      ? `${vm.testId}-action-${vm.pending[0].toLowerCase().replace(/_/g, '-')}`
      : `${vm.testId}-action-respond`;
  }

  /**
   * Whether this row may move its agreed day — the escape hatch, offered at the four states where a day
   * is settled.
   *
   * 🔴 Derived from `canRescheduleRow`, NOT from `vm.pending`, and the difference is the point.
   * `RESCHEDULE` is excluded from the pending set on purpose (see `NON_BLOCKING_ASSOCIATE_TRANSITIONS`):
   * counting it as pending work would put every approved and every awaiting-confirmation visit in
   * *Awaiting you*. It is read off the row rather than off the view-model so the two surfaces that host
   * this component keep needing no change — neither has to re-derive a flag after an in-place commit.
   */
  canReschedule(vm: SchedulingQueueRowVm): boolean {
    return canRescheduleRow(vm.row);
  }

  /** The reschedule control's copy, from the one label map, so the row cannot name it its own way. */
  get rescheduleLabel(): string {
    return pendingActionLabel('RESCHEDULE');
  }

  rescheduleTestId(vm: SchedulingQueueRowVm): string {
    return `${vm.testId}-reschedule`;
  }

  /**
   * Whether the row shows its OWN Reschedule control right now — narrower than {@link canReschedule}.
   *
   * Suppressed whenever the row already carries a pending-action button, i.e. `vm.pending.length >
   * 0`, on the general principle that a second button beside it, opening the same panel, would be two
   * controls doing the same thing — the pattern the template's own comment forbids, not an exception
   * to it.
   *
   * 🔴 **No live state hits that guard today.** `RESCHEDULE`'s legal sources are `TIME_CONFIRMED` and
   * the three retired day-agreed states (`APPROVED`, `PENDING_TIME_PROPOSAL`,
   * `PENDING_TIME_CONFIRMATION` — kept only so an orphaned row still renders), and none of those has a
   * pending `ASSOCIATE_ONLY` move any more: the two-step propose-then-confirm flow that used to leave
   * `PENDING_TIME_PROPOSAL` mid-negotiation is gone with `PROPOSE_TIME`. So this reduces to
   * {@link canReschedule} in every reachable case today. It stays as a guard, not a no-op deleted for
   * tidiness, because the rule it encodes — "don't duplicate the row's one pending action" — is a
   * property of any future state that reintroduces a pending move alongside a reschedulable one, not
   * an artifact of a state that happens not to exist right now.
   */
  showReschedule(vm: SchedulingQueueRowVm): boolean {
    return this.canReschedule(vm) && vm.pending.length === 0;
  }
}
